'use client'

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CheckCircle, AlertCircle } from 'lucide-react'
import { track } from '@/lib/ga'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  interests: z.array(z.string()).default([]),
  message: z.string().optional().default('')
})

// Important: zodResolver uses the schema's input type for the resolver's input
// when the schema contains default()/transform(). To avoid the mismatch where
// input allows undefined but output is coerced, we align React Hook Form's
// field values with the schema INPUT type, and use the OUTPUT type on submit.
type FormInput = z.input<typeof formSchema>
type FormOutput = z.output<typeof formSchema>

const INTEREST_OPTIONS = [
  { value: 'leasing', label: 'Leasing' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'parts', label: 'Parts' },
  { value: 'aircraft-sales', label: 'Aircraft Sales' },
  { value: 'general', label: 'General Inquiry / Other' }
]

export default function UnifiedContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailCaptured, setEmailCaptured] = useState(false)
  const [honeypot, setHoneypot] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      interests: []
    }
  })

  // Format phone number as user types: (555) 123-4567
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length === 0) return ''
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setValue('phone', formatted, { shouldValidate: true })
  }

  // Progressive email capture
  const email = watch('email')
  useEffect(() => {
    if (email && email.includes('@') && !emailCaptured) {
      const timer = setTimeout(async () => {
        try {
          // Silent background save
          await fetch('/api/contact', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, partial: true })
          })
          setEmailCaptured(true)
          console.log('Email captured progressively')
        } catch (err) {
          console.warn('Progressive email capture failed:', err)
        }
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [email, emailCaptured])

  // Calculate form progress
  const name = watch('name')
  const phone = watch('phone')
  const message = watch('message')
  const interests = watch('interests')
  
  let progress = 0
  if (name && name.trim().length > 0) progress += 20
  if (email && email.includes('@') && email.includes('.')) progress += 20
  if (phone && phone.replace(/\D/g, '').length >= 10) progress += 20
  if (interests && interests.length > 0) progress += 20
  if (message && message.trim().length >= 10) progress += 20

  const onSubmit = async (data: FormInput) => {
    // Normalize using schema so downstream always receives defaults (e.g., interests: [])
    const parsed: FormOutput = formSchema.parse(data)
    setIsSubmitting(true)
    setError(null)

    try {
      // Track GA4 event
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'lead_generation',
          event_label: 'unified_contact',
          value: 1
        })
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: parsed.name,
          email: parsed.email,
          phone: parsed.phone,
          interests: parsed.interests,
          message: parsed.message,
          website: honeypot // Honeypot field for spam protection
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit form')
      }

      setIsSuccess(true)
      reset()
      track('lead_form_submit', { method: 'unified_contact' })
      
      // Scroll to top of form to show success message
      setTimeout(() => {
        document.getElementById('form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'There was an error submitting your request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="bg-green-50 border border-green-200 rounded-2xl shadow-xl p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent Successfully</h3>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us! We've received your message and will respond within 24 hours.
          </p>
          <button
            onClick={() => setIsSuccess(false)}
            className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-blue-500 transition-colors"
          >
            Send Another Message
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span 
            className="text-sm font-semibold text-white"
            style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            Form Progress
          </span>
          <span 
            className="text-sm font-semibold text-white"
            style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            {progress}%
          </span>
        </div>
        <div className="w-full h-3 bg-white/80 backdrop-blur-sm rounded-full overflow-hidden shadow-md">
          <div 
            className="h-full bg-gradient-to-r from-[#0a2a66] to-[#0f4c81] transition-all duration-500 ease-out shadow-inner"
            style={{ 
              width: `${progress}%`,
              minWidth: progress > 0 ? '5%' : '0%'
            }}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Honeypot field - hidden from humans, bots will fill it */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Step 1: Contact Information */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 lg:p-8">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-1">Contact Information</h3>
            <p className="text-sm text-gray-600">Please provide your details so we can reach you</p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-600">*</span>
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                placeholder="John Smith"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                placeholder="john@company.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
              {emailCaptured && !errors.email && (
                <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Email saved
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-600">*</span>
              </label>
              <input
                {...register('phone')}
                type="tel"
                id="phone"
                onChange={handlePhoneChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Step 2: Interests and Message */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 lg:p-8">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-1">How Can We Help?</h3>
            <p className="text-sm text-gray-600">Select your interests and tell us about your needs</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Areas of Interest <span className="text-gray-500">(Optional)</span>
              </label>
              <div className="space-y-2">
                {INTEREST_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                    <input
                      {...register('interests')}
                      type="checkbox"
                      value={option.value}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                      {option.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                {...register('message')}
                id="message"
                rows={6}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors resize-none"
                placeholder="Tell us about your aviation needs, questions, or how we can assist you..."
              />
              {errors.message && (
                <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#0a2a66] to-[#0f4c81] hover:from-[#082247] hover:to-[#0d3f6b] px-6 py-4 text-base font-semibold text-white shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
        >
          {isSubmitting ? (
            <>
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Send Message
              <CheckCircle className="h-5 w-5" />
            </>
          )}
        </button>
      </form>
    </div>
  )
}

